import { sortByLabelAsc } from "./sortByProp";

type Indexable = Record<string, unknown>;
type ModelLike = {
  id: string | number;
  name: string;
};
type SelectOption<TValue = string | number> = {
  label: string;
  value: TValue;
};

/**
 * Merge right to left, but don't overwrite higher priority values.
 * undefined < null < value.
 *
 * @param {Object} target - Target object.
 * @param {Object} args - Objects to merge.
 * @return {*} - Merged result.
 */
export function priorityMerge<T extends Indexable>(
  target: T,
  ...args: Indexable[]
): T {
  const mutableTarget = target as Indexable;
  args.forEach((obj) => {
    Object.entries(obj).forEach(([k, right]) => {
      const left = mutableTarget[k];

      switch (right) {
        case undefined:
          if (left === undefined) {
            mutableTarget[k] = undefined;
          }
          break;
        case null:
          if (left === undefined) {
            mutableTarget[k] = null;
          }
          break;
        default:
          mutableTarget[k] = right;
          break;
      }
    });
  });

  return target;
}

export function defaultFormatter<T extends ModelLike>(model: T): string {
  return model.name;
}

export function getSelectOptions<T extends ModelLike>(
  models: Record<string, T> | T[],
  formatter: (model: T) => string = defaultFormatter,
): Array<SelectOption<T["id"]>> {
  return Object.values(models)
    .map((model) => ({
      value: model.id,
      label: formatter(model),
    }))
    .sort(sortByLabelAsc);
}

type Transformable =
  | Indexable
  | unknown[]
  | string
  | number
  | boolean
  | null
  | undefined;

export function transformValues(
  obj: Transformable,
  mapper: (value: unknown) => unknown,
): Indexable {
  if (!obj) {
    return {};
  }

  function transform(
    v: unknown,
    valueMapper: (value: unknown) => unknown,
  ): unknown {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      return transformValues(v as Indexable, valueMapper);
    }

    if (Array.isArray(v)) {
      return v.map((item) => transform(item, valueMapper));
    }

    return valueMapper(v);
  }

  return Object.entries(obj as Indexable).reduce<Indexable>((acc, [k, v]) => {
    if (Array.isArray(v)) {
      acc[k] = v.map((val) => {
        return transform(val, mapper);
      });
    } else {
      acc[k] = transform(v, mapper);
    }

    return acc;
  }, {});
}
